/**
 * DICOM File Upload System with Configurable Logging
 *
 * This module handles the complete workflow for uploading DICOM files:
 * 1. File validation and filtering
 * 2. DICOM metadata extraction
 * 3. ZIP compression
 * 4. S3 upload with presigned URLs (no retries)
 * 5. Server-side commit and database updates
 */

// ============================================================================
// EXTERNAL DEPENDENCIES
// ============================================================================
import * as dicomParser from "dicom-parser";
import JSZip from "jszip";
import RadiologistApi from "../services/ApiServices/RadiologistApi";

// ============================================================================
// LOGGING CONFIGURATION
// ============================================================================
/**
 * Logging configuration - Enable/disable logs for each module
 * Set to true to enable logs, false to disable
 */
const LOG_CONFIG = {
  DICOM_PARSE: true,
  FILE_FILTER: true,
  METADATA: true,
  ZIP: true,
  S3: true,
  S3_PROGRESS: true,
  PRESIGN: true,
  COMMIT: true,
  SUCCESS: true,
  ERROR: true,
  CLEANUP: true,
  UPLOAD_START: true,
  VALIDATION: true,
};

/**
 * Conditional logger based on configuration
 * @param {string} module - Module name to check in LOG_CONFIG
 * @param {...any} args - Arguments to log
 */
const log = (module, ...args) => {
  if (LOG_CONFIG[module]) {
    console.log(`[${module}]`, ...args);
  }
};

/**
 * Conditional error logger
 * @param {string} module - Module name to check in LOG_CONFIG
 * @param {...any} args - Arguments to log
 */
const logError = (module, ...args) => {
  if (LOG_CONFIG.ERROR || LOG_CONFIG[module]) {
    console.error(`[${module}_ERROR]`, ...args);
  }
};

/**
 * Enable/disable logging for specific modules
 * @param {string|Object} moduleOrConfig - Module name or config object
 * @param {boolean} enabled - Whether to enable (ignored if first param is object)
 */
const setLogging = (moduleOrConfig, enabled) => {
  if (typeof moduleOrConfig === "object") {
    Object.assign(LOG_CONFIG, moduleOrConfig);
  } else if (typeof moduleOrConfig === "string") {
    LOG_CONFIG[moduleOrConfig] = enabled;
  }
};

/**
 * Get current logging configuration
 * @returns {Object} Current LOG_CONFIG
 */
const getLoggingConfig = () => ({ ...LOG_CONFIG });

// ============================================================================
// ERROR CLASSES
// ============================================================================
class DicomValidationError extends Error {
  constructor(message, field) {
    super(message);
    this.name = "DicomValidationError";
    this.field = field;
  }
}

class UploadError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = "UploadError";
    this.status = status;
    this.body = body;
  }
}

// ============================================================================
// DICOM METADATA PARSING
// ============================================================================
/**
 * Extracts essential metadata from a DICOM file
 * @param {File} file - DICOM file to parse
 * @returns {Promise<Object>} Parsed metadata object
 * @throws {DicomValidationError} If DICOM parsing fails
 */
const parseDicomMetadata = async (file) => {
  try {
    const buffer = await file.arrayBuffer();
    const byteArray = new Uint8Array(buffer);
    const dataSet = dicomParser.parseDicom(byteArray);

    const accessionNumber = dataSet.string("x00080050") || "";
    const patientID = dataSet.string("x00100020") || "";
    const name = dataSet.string("x00100010") || "Unknown";

    // Calculate birth year from age if available
    const ageStr = dataSet.string("x00101010") || "";
    const birthYear =
      ageStr && !isNaN(parseInt(ageStr, 10))
        ? String(new Date().getFullYear() - parseInt(ageStr, 10))
        : null;

    // Parse acquisition date from DICOM DA format (YYYYMMDD)
    const rawDA = dataSet.string("x00080022") || "";
    const acquisitionDate = parseAcquisitionDate(rawDA);

    log("DICOM_PARSE", "Metadata extracted:", {
      accessionNumber,
      patientID,
      name: name.substring(0, 3) + "***", // Privacy
    });

    return {
      accessionNumber,
      patientID,
      name,
      birthYear,
      acquisitionDate,
    };
  } catch (error) {
    logError("DICOM_PARSE", error);
    throw new DicomValidationError(
      `Failed to parse DICOM metadata: ${error.message}`,
      "metadata"
    );
  }
};

/**
 * Converts DICOM DA format (YYYYMMDD) to ISO date string (YYYY-MM-DD)
 * @param {string} rawDA - Raw DICOM date string
 * @returns {string|null} ISO formatted date or null if invalid
 */
const parseAcquisitionDate = (rawDA) => {
  if (!rawDA || typeof rawDA !== "string") return null;

  const match = /^(\d{4})(\d{2})(\d{2})$/.exec(rawDA);
  if (!match) return null;

  const [, year, month, day] = match;

  // Basic date validation
  if (month < "01" || month > "12" || day < "01" || day > "31") {
    return null;
  }

  return `${year}-${month}-${day}`;
};

// ============================================================================
// FILE VALIDATION AND PROCESSING
// ============================================================================
/**
 * List of potentially dangerous file extensions to block
 */
const BLOCKED_EXTENSIONS = [
  // Executables
  'exe', 'bat', 'cmd', 'com', 'scr', 'msi', 'dll', 'app', 'dmg',
  // Scripts
  'js', 'py', 'rb', 'php', 'pl', 'sh', 'ps1', 'vbs', 'jar',
  // Web files
  'html', 'htm', 'css', 'xml',
  // Archives (could contain malicious files)
  'zip', 'rar', '7z', 'tar', 'gz', 'bz2',
  // Documents with macros
  'docm', 'xlsm', 'pptm',
  // Config/data that could be malicious
  'json', 'ini', 'cfg', 'conf', 'sql'
];

/**
 * Common DICOM file extensions (for informational logging)
 */
const DICOM_EXTENSIONS = ['dcm', 'dic', 'ima', 'dicom'];

/**
 * Filters and validates DICOM files from file input
 * @param {FileList} fileList - Files from input element
 * @returns {File[]} Array of valid DICOM files
 * @throws {DicomValidationError} If no valid files found or blocked files detected
 */
const validateAndFilterDicomFiles = (fileList) => {
  const files = Array.from(fileList || []);
  
  // Check for blocked file types
  const blockedFiles = files.filter(file => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    return extension && BLOCKED_EXTENSIONS.includes(extension);
  });

  if (blockedFiles.length > 0) {
    throw new DicomValidationError(
      `Blocked file types detected: ${blockedFiles.map(f => f.name).join(', ')}. Only medical imaging files are allowed.`,
      "blocked_files"
    );
  }

  // Accept files with DICOM extensions OR files with no extension
  const potentialDicomFiles = files.filter(file => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    // Accept if: has known DICOM extension, no extension, or unknown extension (will be validated during DICOM parsing)
    return !extension || DICOM_EXTENSIONS.includes(extension) || !BLOCKED_EXTENSIONS.includes(extension);
  });

  log("FILE_FILTER", `total=${files.length}, potential_dicom=${potentialDicomFiles.length}, blocked=${blockedFiles.length}`);

  if (!potentialDicomFiles.length) {
    throw new DicomValidationError(
      "No valid medical imaging files found in selection",
      "files"
    );
  }

  return potentialDicomFiles;
};

/**
 * Validates that all DICOM files belong to the same patient/study
 * @param {File[]} dicomFiles - Array of DICOM files to validate
 * @returns {Promise<Object>} Consistent metadata from first file
 * @throws {DicomValidationError} If validation fails or metadata is inconsistent
 */
const validateDicomConsistency = async (dicomFiles) => {
  log("METADATA", "Parsing first DICOM file");

  const metadata = await parseDicomMetadata(dicomFiles[0]);

  // Validate required fields
  if (!metadata.accessionNumber) {
    throw new DicomValidationError(
      "Accession number (0008,0050) is missing from DICOM file",
      "accessionNumber"
    );
  }

  if (!metadata.patientID) {
    throw new DicomValidationError(
      "Patient ID (0010,0020) is missing from DICOM file",
      "patientID"
    );
  }

  log("VALIDATION", "Metadata validation passed");

  // NOTE: For production, you may validate all files for consistency.

  return metadata;
};

// ============================================================================
// ZIP COMPRESSION
// ============================================================================
/**
 * Compresses DICOM files into a ZIP archive
 * @param {File[]} dicomFiles - Array of DICOM files to compress
 * @returns {Promise<Blob>} ZIP file as blob
 */
const createDicomZip = async (dicomFiles) => {
  log("ZIP", `Compressing ${dicomFiles.length} files`);

  const zip = new JSZip();

  dicomFiles.forEach((file) => {
    const path = file.webkitRelativePath || file.name;
    zip.file(path, file);
  });

  const zipBlob = await zip.generateAsync({ type: "blob" });
  log("ZIP", `Complete: ${(zipBlob.size / 1024 / 1024).toFixed(2)} MB`);

  return zipBlob;
};

// ============================================================================
// S3 UPLOAD UTILITIES
// ============================================================================
/**
 * Extracts query parameter from URL
 * @param {string} url - URL to parse
 * @param {string} key - Parameter key to extract
 * @returns {string|null} Parameter value or null
 */
const getQueryParam = (url, key) => {
  try {
    return new URL(url).searchParams.get(key);
  } catch {
    return null;
  }
};

/**
 * Builds S3 headers from presigned URL parameters
 * @param {string} presignedUrl - S3 presigned URL
 * @returns {Object} Headers object for S3 request
 */
const buildS3Headers = (presignedUrl) => {
  const headers = {};

  const contentType = getQueryParam(presignedUrl, "Content-Type");
  const acl = getQueryParam(presignedUrl, "x-amz-acl");

  if (contentType) headers["Content-Type"] = decodeURIComponent(contentType);
  if (acl) headers["x-amz-acl"] = decodeURIComponent(acl);

  return headers;
};

/**
 * Uploads blob to S3 using XMLHttpRequest (avoids auth header contamination)
 * @param {string} presignedUrl - S3 presigned upload URL
 * @param {Blob} blob - File blob to upload
 * @returns {Promise<string>} ETag from successful upload
 */
const uploadToS3 = (presignedUrl, blob, onProgress = null) => {
  return new Promise((resolve, reject) => {
    const headers = buildS3Headers(presignedUrl);
    const xhr = new XMLHttpRequest();

    xhr.open("PUT", presignedUrl, true);
    xhr.withCredentials = false; // Prevent auth header leakage

    // Set required headers
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    // Progress tracking
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentage = Math.round((event.loaded / event.total) * 100);
        log("S3_PROGRESS", `${percentage}%`);
        // Map S3 upload progress (0-100%) to overall progress (35-80%)
        const adjustedPercentage = 35 + Math.round(percentage * 0.45);
        onProgress?.(adjustedPercentage, `Uploading study... ${percentage}%`);
      }
    };

    // Success handler
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = (xhr.getResponseHeader("ETag") || "").replace(/"/g, "");
        log("S3", "Upload successful");
        resolve(etag);
      } else {
        reject(
          new UploadError(
            `S3 upload failed with status ${xhr.status}`,
            xhr.status,
            xhr.responseText
          )
        );
      }
    };

    // Error handler
    xhr.onerror = () => {
      reject(new UploadError("S3 upload network error", 0, null));
    };

    xhr.send(blob);
  });
};

/**
 * Extract a backend-provided error/message string from any Axios-like error
 * @param {any} errLike
 * @returns {string|null}
 */
const extractBackendMessage = (errLike) => {
  const data =
    errLike?.response?.data ||
    errLike?.originalError?.response?.data ||
    errLike?.data ||
    errLike?.body;
  if (!data) return null;
  if (typeof data === "string") return data;
  return data.error || data.message || null;
};

/**
 * Uploads ZIP to S3 via presigned URL (no retries)
 * @param {string} accessionNumber
 * @param {string} fileName
 * @param {Blob} zipBlob
 * @returns {Promise<{etag: string, key: string}>}
 */
const uploadZipToS3 = async (accessionNumber, fileName, zipBlob, onProgress = null) => {
  log("S3", "Starting upload");
  log("PRESIGN", "Requesting pre-signed PUT URL");
  ;
  let payload;
  try {
    payload = await RadiologistApi.presignDicom({
      accessionNumber,
      fileName,
      size: zipBlob.size,
    });
  } catch (e) {
    const msg = extractBackendMessage(e) || e?.message || "Presign failed";
    console.error("[PRESIGN_ERROR]", msg);
    throw new UploadError(msg, e?.response?.status, e?.response?.data);
  }

  if (!payload) {
    console.error("[PRESIGN_ERROR] presignDicom returned undefined/null");
    throw new UploadError("Presign failed", 0, null);
  }

  const { uploadUrl, key } = payload;
  if (!uploadUrl || !key) {
    const serverMsg = payload.error || payload.message || "Presign failed";
    console.error("[PRESIGN_ERROR]", serverMsg);
    throw new UploadError(serverMsg, 400, payload);
  }

  const etag = await uploadToS3(uploadUrl, zipBlob, onProgress);
  return { etag, key };
};

// ============================================================================
// DATA PREPARATION
// ============================================================================
/**
 * Creates patient data object for database
 * @param {Object} metadata - DICOM metadata
 * @param {string} baseFolder - Base folder name
 * @returns {Object} Patient data object
 */
const createPatientData = (metadata, baseFolder) => {
  return {
    patientID: metadata.patientID,
    name: metadata.name,
    birthYear: metadata.birthYear,
    acquisitionDate: metadata.acquisitionDate, // normalized or null
    lastExamStatus: "Unknown",
    lastExamId: metadata.accessionNumber,
    latestFindingScore: 0,
    consentGiven: false,
    HCP: "",
    email: "Unknown",
    phoneCode: "Unknown",
    phoneNumber: "Unknown",
    fileName: baseFolder,
    accessionNumber: metadata.accessionNumber,
  };
};

/**
 * Creates case data object for database
 * @param {Object} metadata - DICOM metadata
 * @param {string} baseFolder - Base folder name
 * @returns {Object} Case data object
 */
const createCaseData = (metadata, baseFolder) => {
  return {
    examId: metadata.accessionNumber,
    patientId: metadata.patientID,
    findingScore: 0,
    examImagesId: null,
    status: "pending",
    fileName: baseFolder,
  };
};

/**
 * Determines base folder name from file path
 * @param {File} firstFile - First DICOM file
 * @returns {string} Base folder name
 */
const getBaseFolderName = (firstFile) => {
  return firstFile.webkitRelativePath
    ? firstFile.webkitRelativePath.split("/")[0]
    : firstFile.name.replace(/\.[^/.]+$/, "");
};

// ============================================================================
// ERROR HANDLING
// ============================================================================
/**
 * Formats upload errors for handling.
 * Prefers backend-provided messages; does not synthesize client strings.
 * @param {Error} error - Error object
 * @returns {Object} Formatted error object with type and message
 */
const formatUploadError = (error) => {
  const resp = error?.response || error?.originalError?.response || null;
  const data = resp?.data ?? error?.data ?? error?.body;

  // ONLY use backend-provided text if available
  let message = null;
  if (typeof data === "string") message = data;
  else if (data?.error) message = data.error;
  else if (data?.message) message = data.message;

  // For validation errors (local), fall back to the thrown message
  if (!message && error instanceof DicomValidationError) {
    message = error.message;
  }

  const type = error instanceof DicomValidationError ? "VALIDATION" : "UPLOAD";

  return {
    type,
    message, // may be null if not provided by backend
    details: { status: resp?.status, body: data },
    originalError: error,
  };
};

// ============================================================================
// MAIN UPLOAD FUNCTION
// ============================================================================
/**
 * Main DICOM folder upload handler
 * Coordinates the complete upload workflow:
 * 1. Validates DICOM files
 * 2. Extracts metadata
 * 3. Creates ZIP archive
 * 4. Uploads to S3 (no retries)
 * 5. Commits to database
 * 6. Updates UI
 *
 * @param {Event} event - File input change event
 * @param {Function} setIsLoading - Loading state setter
 * @param {Function} setData - Data update function
 * @param {Object} filtersObj - Current filter object
 * @param {Function} getAllPatients - Function to refresh patient data
 * @param {Function} onError - Optional custom error handler
 * @returns {Promise<Object>} Result object with success status and data/error
 */
export const handleFolderSelect = async (
  event,
  setIsLoading,
  setData,
  filtersObj,
  getAllPatients,
  onError = null,
  onProgress = null
) => {
  log("UPLOAD_START", "DICOM folder upload initiated");
  setIsLoading(true);

  const result = {
    success: false,
    data: null,
    error: null,
  };
  
  try {
    // Step 1: Validate and filter files
    onProgress?.(5, "Preparing your study files...");
    const dicomFiles = validateAndFilterDicomFiles(event.target.files);

    // Step 2: Extract and validate metadata
    onProgress?.(15, "Reading study information...");
    const metadata = await validateDicomConsistency(dicomFiles);
    const baseFolder = getBaseFolderName(dicomFiles[0]);

    // Step 3: Create ZIP archive
    onProgress?.(25, "Compressing study data...");
    const zipBlob = await createDicomZip(dicomFiles);

    // Step 4: Upload to S3 (no retry)
    onProgress?.(35, "Uploading study to secure servers...");
    log("S3", "Beginning upload process");
    const { etag, key } = await uploadZipToS3(
      metadata.accessionNumber,
      baseFolder,
      zipBlob,
      onProgress
    );

    // Step 5: Prepare database payloads
    const patientData = createPatientData(metadata, baseFolder);
    const caseData = createCaseData(metadata, baseFolder);

    // Step 6: Commit to server (database + SQS + EC2 processing)
    onProgress?.(85, "Processing study for analysis...");
    log("COMMIT", "Finalizing upload on server");
    try {
      await RadiologistApi.commitDicom({
        accessionNumber: metadata.accessionNumber,
        key,
        fileName: baseFolder,
        patientData,
        caseData,
        etag, // For integrity verification
      });
    } catch (e) {
      const msg = extractBackendMessage(e);
      if (msg) logError("COMMIT", `Backend error: ${msg}`);
      else logError("COMMIT", e);
      throw e; // bubble to outer catch
    }

    // Step 7: Refresh patient data in UI
    onProgress?.(95, "Updating patient records...");
    log("SUCCESS", "Upload complete, refreshing patient list");
    const updatedPatients = await getAllPatients(filtersObj);
    setData(updatedPatients);

    onProgress?.(100, "Study uploaded successfully!");
    result.success = true;
    result.data = {
      accessionNumber: metadata.accessionNumber,
      patientID: metadata.patientID,
      fileName: baseFolder,
    };
  } catch (error) {
    const formattedError = formatUploadError(error);

    // Print backend message (if any) to console for quick debugging
    if (formattedError.message) {
      logError("UPLOAD_WORKFLOW", `Backend error: ${formattedError.message}`);
    } else {
      logError("UPLOAD_WORKFLOW", formattedError);
    }

    result.error = formattedError;

    if (onError) onError(formattedError);
  } finally {
    // Cleanup
    if (event?.target) {
      event.target.value = ""; // Allow reselection of same files
    }
    setIsLoading(false);
    log("CLEANUP", "Upload process complete");
  }

  return result;
};

// ============================================================================
// EXPORTS
// ============================================================================

// Default export
export default handleFolderSelect;

// Named exports
export {
  // Helper functions
  parseDicomMetadata,
  createDicomZip,
  uploadToS3,
  validateAndFilterDicomFiles,
  validateDicomConsistency,
  uploadZipToS3,

  // Utilities
  parseAcquisitionDate,
  buildS3Headers,
  createPatientData,
  createCaseData,
  getBaseFolderName,
  formatUploadError,

  // Logging control
  setLogging,
  getLoggingConfig,

  // Error classes
  DicomValidationError,
  UploadError,
};
